import { useState } from "react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./usage-bar.i18n";

interface UsageBarProps {
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  cost?: number;
  durationMs?: number;
  toolCalls?: number;
}

function formatTokens(n: number): string {
  if (n >= 500_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function UsageBar({
  model,
  inputTokens,
  outputTokens,
  cost,
  durationMs,
  toolCalls,
}: UsageBarProps) {
  const t = useT(T);
  const hasData = inputTokens != null || durationMs != null || cost != null;
  if (!hasData) return null;

  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1.5 text-[10px] text-muted-foreground/60 leading-none items-center">
      {model && <span>{model}</span>}
      {inputTokens != null && outputTokens != null && (
        <span>
          &#8593;{formatTokens(inputTokens)} &#8595;{formatTokens(outputTokens)}
        </span>
      )}
      {durationMs != null && <span>{(durationMs / 1000).toFixed(1)}s</span>}
      {toolCalls != null && toolCalls > 0 && <span>{t.tools(toolCalls)}</span>}
      {cost != null && cost > 0 && <span>€{cost.toFixed(4)}</span>}
    </div>
  );
}

// ─── Context bar for chat footer ────────────────────────────

export type ContextInfo = {
  systemPromptTokens: number;
  historyTokens: number;
  /** Subset of historyTokens — tokens contributed by replayed tool_calls + tool results. */
  historyToolTokens?: number;
  historyMessages: number;
  toolDefsTokens: number;
  builtinTools: number;
  mcpTools: number;
  maxContextTokens: number;
};

type ChatTotals = {
  totalInput: number;
  totalOutput: number;
  totalCost: number;
  peakContext: number;
};

export function ContextBar({
  context,
  chatTotals,
}: {
  context?: ContextInfo | null;
  chatTotals?: ChatTotals;
}) {
  const t = useT(T);
  const [showDetails, setShowDetails] = useState(false);
  const maxTokens = context?.maxContextTokens || 256000;

  // Estimated next-message context = system prompt + messages + tool defs
  const estimatedContext = context
    ? context.systemPromptTokens + context.historyTokens + context.toolDefsTokens
    : 0;

  if (estimatedContext === 0 && !chatTotals) return null;

  const percent = (estimatedContext / maxTokens) * 100;
  const isNearLimit = percent >= 80;

  return (
    <div className="px-4 py-1">
      <button
        type="button"
        className="w-full flex items-center gap-2"
        onClick={() => setShowDetails((v) => !v)}
      >
        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isNearLimit ? "bg-destructive" : percent > 50 ? "bg-warning" : "bg-primary/40"
            }`}
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
        <span
          className={`text-[9px] whitespace-nowrap ${isNearLimit ? "text-destructive" : "text-muted-foreground/50"}`}
        >
          {formatTokens(estimatedContext)}/{formatTokens(maxTokens)}
          {isNearLimit && t.nearLimit}
        </span>
      </button>

      {showDetails && (
        <button
          type="button"
          className="w-full text-left mt-1 px-1 py-2 text-[10px] text-muted-foreground/70 space-y-0.5 border-t border-muted/30 cursor-pointer"
          onClick={() => setShowDetails(false)}
        >
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground/40 mb-1">
            {t.nextMessageContext}
          </div>
          {context && (
            <>
              <div className="flex justify-between">
                <span>{t.systemPrompt}</span>
                <span>{formatTokens(context.systemPromptTokens)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.messages(context.historyMessages)}</span>
                <span>{formatTokens(context.historyTokens)}</span>
              </div>
              {context.historyToolTokens != null && context.historyToolTokens > 0 && (
                <>
                  <div className="flex justify-between text-muted-foreground/50 pl-2">
                    <span>{t.text}</span>
                    <span>
                      {formatTokens(Math.max(0, context.historyTokens - context.historyToolTokens))}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground/50 pl-2">
                    <span>{t.toolReplay}</span>
                    <span>{formatTokens(context.historyToolTokens)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span>{t.toolDefs(context.builtinTools, context.mcpTools)}</span>
                <span>{formatTokens(context.toolDefsTokens)}</span>
              </div>
              <div className="flex justify-between font-medium text-muted-foreground/90 pt-1 border-t border-muted/20">
                <span>{t.totalContext}</span>
                <span>{formatTokens(estimatedContext)}</span>
              </div>
            </>
          )}
          {chatTotals && (
            <>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground/40 mt-2 mb-1">
                {t.chatTotals}
              </div>
              <div className="flex justify-between">
                <span>{t.sentReceived}</span>
                <span>
                  {formatTokens(chatTotals.totalInput)} / {formatTokens(chatTotals.totalOutput)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t.peakContext}</span>
                <span>{formatTokens(chatTotals.peakContext)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.cost}</span>
                <span>€{chatTotals.totalCost.toFixed(4)}</span>
              </div>
            </>
          )}
        </button>
      )}
    </div>
  );
}
